/** Filters, sort and cursor of a list query; part of its key so every combination caches on its own. */
export type ServiceListParams = Readonly<Record<string, string | number | boolean | readonly string[] | null | undefined>>;

const ROOT = "service";

/**
 * TanStack Query keys for the Service screens. Every key starts with the workspace id (like `engageKeys`) and nests
 * under its resource, so `invalidateQueries({ queryKey: serviceKeys.tickets(ws) })` refreshes the lists, the open
 * ticket and its timeline together.
 */
export const serviceKeys = {
  all: (ws: string) => ["ws", ws, ROOT] as const,

  tickets: (ws: string) => [...serviceKeys.all(ws), "tickets"] as const,
  ticketLists: (ws: string) => [...serviceKeys.tickets(ws), "list"] as const,
  ticketList: (ws: string, params: ServiceListParams) => [...serviceKeys.ticketLists(ws), params] as const,
  ticket: (ws: string, id: string) => [...serviceKeys.tickets(ws), "detail", id] as const,
  ticketTimeline: (ws: string, id: string) => [...serviceKeys.ticket(ws, id), "timeline"] as const,

  organisations: (ws: string) => [...serviceKeys.all(ws), "organisations"] as const,
  organisationList: (ws: string, params: ServiceListParams) => [...serviceKeys.organisations(ws), "list", params] as const,
  organisation: (ws: string, id: string) => [...serviceKeys.organisations(ws), "detail", id] as const,

  contacts: (ws: string) => [...serviceKeys.all(ws), "contacts"] as const,
  contactList: (ws: string, params: ServiceListParams) => [...serviceKeys.contacts(ws), "list", params] as const,
  contact: (ws: string, id: string) => [...serviceKeys.contacts(ws), "detail", id] as const,

  settings: (ws: string) => [...serviceKeys.all(ws), "settings"] as const,
  settingsTab: (ws: string, tab: string) => [...serviceKeys.settings(ws), tab] as const,

  reports: (ws: string) => [...serviceKeys.all(ws), "reports"] as const,
  report: (ws: string, tab: string, params: ServiceListParams) => [...serviceKeys.reports(ws), tab, params] as const,

  quarantine: (ws: string) => [...serviceKeys.all(ws), "quarantine"] as const,
  quarantineList: (ws: string, status: string) => [...serviceKeys.quarantine(ws), "list", status] as const,
  quarantineItem: (ws: string, id: string) => [...serviceKeys.quarantine(ws), "item", id] as const,

  imports: (ws: string) => [...serviceKeys.all(ws), "imports"] as const,
  importJob: (ws: string, id: string) => [...serviceKeys.imports(ws), id] as const,

  /** After-sales (SD-S3-10): assets with their timeline, documents, parts and tickets nest under one asset's key. */
  assets: (ws: string) => [...serviceKeys.all(ws), "assets"] as const,
  assetList: (ws: string, params: ServiceListParams) => [...serviceKeys.assets(ws), "list", params] as const,
  asset: (ws: string, id: string) => [...serviceKeys.assets(ws), "detail", id] as const,
  assetPart: (ws: string, id: string, part: string) => [...serviceKeys.asset(ws, id), part] as const,
  assetLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.assets(ws), "lookup", name, params] as const,

  contractsOf: (ws: string) => [...serviceKeys.all(ws), "contracts"] as const,
  contractList: (ws: string, params: ServiceListParams) => [...serviceKeys.contractsOf(ws), "list", params] as const,
  contractDetail: (ws: string, id: string) => [...serviceKeys.contractsOf(ws), "detail", id] as const,
  contractPart: (ws: string, id: string, part: string) => [...serviceKeys.contractDetail(ws, id), part] as const,

  installations: (ws: string) => [...serviceKeys.all(ws), "installations"] as const,
  installationList: (ws: string, params: ServiceListParams) => [...serviceKeys.installations(ws), "list", params] as const,
  installation: (ws: string, id: string) => [...serviceKeys.installations(ws), "detail", id] as const,
  installationPart: (ws: string, id: string, part: string) => [...serviceKeys.installation(ws, id), part] as const,

  /** Field visits and RMAs (SD-S3-10 part 2): the board, lists and one visit / RMA with its parts nest under their own roots. */
  visits: (ws: string) => [...serviceKeys.all(ws), "visits"] as const,
  visitBoard: (ws: string, params: ServiceListParams) => [...serviceKeys.visits(ws), "board", params] as const,
  visitList: (ws: string, params: ServiceListParams) => [...serviceKeys.visits(ws), "list", params] as const,
  visit: (ws: string, id: string) => [...serviceKeys.visits(ws), "detail", id] as const,
  visitPart: (ws: string, id: string, part: string) => [...serviceKeys.visit(ws, id), part] as const,
  visitLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.visits(ws), "lookup", name, params] as const,

  rmas: (ws: string) => [...serviceKeys.all(ws), "rmas"] as const,
  rmaList: (ws: string, params: ServiceListParams) => [...serviceKeys.rmas(ws), "list", params] as const,
  rma: (ws: string, id: string) => [...serviceKeys.rmas(ws), "detail", id] as const,
  rmaPart: (ws: string, id: string, part: string) => [...serviceKeys.rma(ws, id), part] as const,
  rmaLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.rmas(ws), "lookup", name, params] as const,

  /** Commerce (SD-S2-21): orders with their approvals, quotes and cancellations; the catalogue; the commerce profile. */
  orders: (ws: string) => [...serviceKeys.all(ws), "orders"] as const,
  orderList: (ws: string, params: ServiceListParams) => [...serviceKeys.orders(ws), "list", params] as const,
  order: (ws: string, id: string) => [...serviceKeys.orders(ws), "detail", id] as const,
  orderPart: (ws: string, id: string, part: string, params: ServiceListParams = {}) => [...serviceKeys.order(ws, id), part, params] as const,
  orderApprovals: (ws: string, params: ServiceListParams) => [...serviceKeys.orders(ws), "approvals", params] as const,
  orderQuotes: (ws: string, params: ServiceListParams) => [...serviceKeys.orders(ws), "quotes", params] as const,
  orderQuote: (ws: string, id: string) => [...serviceKeys.orders(ws), "quote", id] as const,
  orderLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.orders(ws), "lookup", name, params] as const,

  catalogue: (ws: string) => [...serviceKeys.all(ws), "catalogue"] as const,
  catalogueList: (ws: string, params: ServiceListParams) => [...serviceKeys.catalogue(ws), "list", params] as const,
  catalogueItem: (ws: string, id: string) => [...serviceKeys.catalogue(ws), "item", id] as const,
  catalogueSyncJobs: (ws: string) => [...serviceKeys.catalogue(ws), "sync-jobs"] as const,
  catalogueVisibility: (ws: string, organisationId: string | null = null) => [...serviceKeys.catalogue(ws), "visibility", organisationId] as const,

  commerce: (ws: string) => [...serviceKeys.all(ws), "commerce"] as const,
  commerceProfiles: (ws: string) => [...serviceKeys.commerce(ws), "profiles"] as const,
  commerceProfile: (ws: string, id: string) => [...serviceKeys.commerce(ws), "profile", id] as const,
  commercePart: (ws: string, id: string, part: string) => [...serviceKeys.commerceProfile(ws, id), part] as const,
  commerceLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.commerce(ws), "lookup", name, params] as const,

  /** Spare-part requests (SD-S4-06): the lists, one request, and the lookups of its form. */
  spares: (ws: string) => [...serviceKeys.all(ws), "spares"] as const,
  spareList: (ws: string, params: ServiceListParams) => [...serviceKeys.spares(ws), "list", params] as const,
  spare: (ws: string, id: string) => [...serviceKeys.spares(ws), "detail", id] as const,
  spareLookups: (ws: string, name: string, params: ServiceListParams = {}) => [...serviceKeys.spares(ws), "lookup", name, params] as const,

  /** RMA replacement (SD-S4-06): the workspace's mode, and what one replaced RMA produced. */
  rmaReplacementMode: (ws: string) => [...serviceKeys.rmas(ws), "replacement-mode"] as const,
  rmaReplacement: (ws: string, rmaId: string) => [...serviceKeys.rmas(ws), "replacement", rmaId] as const,

  /** Lead to organisation conversion (ORG-019): the preview of one lead, and the lead an organisation came from. */
  leadConversion: (ws: string, leadId: string) => [...serviceKeys.all(ws), "lead-conversion", leadId] as const,
  originatingLead: (ws: string, organisationId: string) => [...serviceKeys.organisation(ws, organisationId), "originating-lead"] as const,

  /** What a ticket's context panel reads beyond the ticket itself (child tickets, contract cover, equipment). */
  ticketPart: (ws: string, number: string, part: string, params: ServiceListParams = {}) => [...serviceKeys.ticket(ws, number), part, params] as const,
} as const;
